import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FileText } from 'lucide-react';

import { getMyPolicyTasks } from '../../services/policyService';
import { formatDateTime } from '../../utils/Utils';

const Container = styled.div``;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
`;

const TaskCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  cursor: pointer;
  transition: box-shadow 0.2s;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  &:hover { box-shadow: 0 2px 8px rgba(0, 66, 66, 0.12); }
`;

const IconCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.primary.main + '15'};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #004242;
  margin-bottom: 4px;
`;

const CardDesc = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
  margin-bottom: 8px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 3 || $status === 'completed' ? '#16a34a15' :
    $status === 4 || $status === 'overdue' ? '#ef444415' :
    '#ca8a0415'};
  color: ${({ $status }) =>
    $status === 3 || $status === 'completed' ? '#16a34a' :
    $status === 4 || $status === 'overdue' ? '#ef4444' :
    '#ca8a04'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
`;

export default function MyPolicyTasksPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyPolicyTasks()
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setTasks(items);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleClick = useCallback((task) => {
    history.push(`/my-policy-tasks/${task.id}`);
  }, [history]);

  const getStatusLabel = useCallback((status) => {
    // 0=Pending, 1=DocumentOpened, 2=QuizStarted, 3=Completed, 4=Overdue
    switch (status) {
      case 3: case 'completed': return t('policy.taskCompleted');
      case 4: case 'overdue': return t('policy.taskOverdue');
      case 1: case 'opened': return t('policy.taskOpened');
      case 2: case 'quizStarted': return t('policy.taskQuizStarted');
      default: return t('policy.taskPending');
    }
  }, [t]);

  if (loading) return null;

  return (
    <Container>
      <Header>
        <Title>{t('policy.myTasks')}</Title>
      </Header>

      {tasks.length === 0 ? (
        <EmptyState>{t('common.noRecords')}</EmptyState>
      ) : (
        <CardGrid>
          {tasks.map((task) => (
            <TaskCard key={task.id} onClick={() => handleClick(task)}>
              <IconCircle>
                <FileText size={20} />
              </IconCircle>
              <CardContent>
                <CardTitle>{task.policyDocumentTitle || task.documentTitle || task.title}</CardTitle>
                {(task.policyDocumentDescription || task.documentDescription) && (
                  <CardDesc>{task.policyDocumentDescription || task.documentDescription}</CardDesc>
                )}
                <CardMeta>
                  {t('policy.dueDate')}: {formatDateTime(task.dueDate, i18n.language)}
                </CardMeta>
                <StatusBadge $status={task.status}>
                  {getStatusLabel(task.status)}
                </StatusBadge>
              </CardContent>
            </TaskCard>
          ))}
        </CardGrid>
      )}
    </Container>
  );
}
